/* eslint-disable import/no-cycle */

import React, { createContext, useEffect, useMemo, useState } from 'react';

import { debounce, mergeClasses } from '@utils';

import Account from './components/Account';
import Cart from './components/Cart';
import Favourites from './components/Favourites';
import Menu from './components/Menu';

import Navigation from './modules/Navigation/Navigation';
import Search from './modules/Search/Search';
import TinyBanner from './modules/TinyBanner/TinyBanner';

export const HeaderContext = createContext({});

export default function Header({
  navigationItems,
  headerLogo,
  isLoggedIn,
  firstName,
  options: { locale }
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [isHeaderVisible, toggleHeader] = useState(true);

  const [isMobile, setIsMobile] = useState(false);

  const contextValue = useMemo(
    () => ({
      isLoggedIn,
      firstName
    }),
    [isLoggedIn, firstName]
  );

  useEffect(() => {
    const updateSize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', debounce(updateSize, 100));
    updateSize();

    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    const handleScroll = debounce(() => {
      const currentScrollTop =
        window.pageYOffset || document.documentElement.scrollTop;

      const offset = 10;

      if (currentScrollTop > lastScrollTop + offset && currentScrollTop > 200) {
        toggleHeader(false);
      } else if (
        currentScrollTop < lastScrollTop - offset ||
        currentScrollTop < 100
      ) {
        toggleHeader(true);
      }

      setLastScrollTop(currentScrollTop <= 0 ? 0 : currentScrollTop);
    });

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollTop]);

  return (
    <HeaderContext.Provider value={contextValue}>
      <div
        className={mergeClasses(
          'fixed inset-x-0 top-0 z-[5000] transition-transform duration-200 ease-in-out',
          {
            '-translate-y-full': !isHeaderVisible
          }
        )}
      >
        <div className="relative flex h-16 w-full items-center justify-between gap-x-2 bg-white px-4 lg:px-6">
          <div>{headerLogo}</div>
          <div className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 transform lg:block lg:w-2/5">
            <Search
              options={{
                locale
              }}
            />
          </div>
          <div className="flex shrink-0 items-center">
            <div className="lg:hidden">
              <Search
                options={{
                  locale
                }}
              />
            </div>
            <Account />
            <Favourites isVisible={isLoggedIn && !isMobile} />
            <Cart />
            <Menu setIsMenuOpen={setIsMenuOpen} />
          </div>
        </div>
        <Navigation
          navigationItems={navigationItems?.top}
          isMobile={isMobile}
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
        />
        <TinyBanner />
      </div>
    </HeaderContext.Provider>
  );
}
